import {
	ReactElement,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	ChangeTriggerKind,
	ContextActionBar,
	FloatingActionButton,
	getPropertySortText,
	InlineOptionSelectorItem,
	OptionsMenu,
	OptionsMenuItem,
	SearchInputForm,
	SearchPlaceholder,
	ToolbarAction,
	ToolbarRegularAction
} from "client/Components";
import {
	MediaQueryKind,
	useForceRerender,
	useUserTimezone,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { AnnouncementPreviewItemModel } from "client/Api/Hub/Announcements/announcementViewer";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import { localize } from "client_legacy/ndaw/culture";
import {
	ActiveFilter,
	getFollowCategorySubscriptionStatus,
	HubDialogContext,
	HubItemIsNewFilter
} from "../../common";
import { useFollowCategoryAction } from "../../Sidebar/useFollowCategoryAction";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { HubOverviewLayout, HubOverviewLayoutSectionContainerHandle } from "../../HubOverviewLayout";
import {
	ActionKind,
	buildActionsForTopBar
} from "client_legacy/ndaw/actions";
import { OverviewSections } from "./OverviewList/OverviewSections";
import { TimeZone } from "client/DateAndTime/timezones";
import { useHubManagementDialog } from "../../Management/HubManagementDialog/HubManagementDialog";
import { CreateHubItemDialog } from "../../CreateHubItemDialog/CreateHubItemDialog";
import { isNotNil } from "client/Misc/utils";
import { useHubItemsChangedListener } from "../../useHubItemsChangedListener";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export enum SortByValue {
	DateCreatedNew = 1,
	DateCreatedOld = 2,
	MostPopular = 3
}

type SortByOption = InlineOptionSelectorItem<DataSourceSortableState<AnnouncementPreviewItemModel>>;

export const sortOptions: readonly SortByOption[] = [
	{
		text: getPropertySortText("_PublicationDate", "_NewToOld"),
		value: {
			direction: DataSourceSortDirection.Descending,
			propertyName: "publicationStart",
		},
		valueTrackingId: "publication-date-new-old"
	},
	{
		text: getPropertySortText("_PublicationDate", "_OldToNew"),
		value: {
			direction: DataSourceSortDirection.Ascending,
			propertyName: "publicationStart"
		},
		valueTrackingId: "publication-date-old-new"
	},
	{
		text: localize("_MostPopular"),
		value: {
			direction: DataSourceSortDirection.Descending,
			propertyName: "viewsCount"
		},
		valueTrackingId: "most-popular"
	}
];

export interface SelectedAuthor {
	name: string;
	id: number;
}

export type OverviewProps = {
	selectedCategoryId: number | null;
	activeFilter: ActiveFilter | null;
	onActiveFilterChange: (filter: ActiveFilter | null) => void;
	categories: ReadonlyArray<HubItemCategoryModel>;
	onNavigateToItem(id: number): void;
	getCategory: (categoryId: number | null) => HubItemCategoryModel | undefined;
	onCategoryCreated: () => void;
	hubItemKindsCanCreateNewCategoriesFor: ReadonlyArray<HubItemKind>;
	searchTerm: VersionedValue<string>;
	onSearchTermChange: (value: VersionedValue<string>) => void;
	sorting: DataSourceSortableState<AnnouncementPreviewItemModel>;
	onSortingChange: (value: DataSourceSortableState<AnnouncementPreviewItemModel>) => void;
	canOpenHubManagement: boolean;
	isNewFilter: HubItemIsNewFilter;
	onIsNewFilterChange: (value: HubItemIsNewFilter) => void;
};

export const Overview = (props: OverviewProps): ReactElement => {
	const {
		categories,
		selectedCategoryId,
		activeFilter,
		onActiveFilterChange,
		getCategory,
		onNavigateToItem,
		onCategoryCreated,
		hubItemKindsCanCreateNewCategoriesFor,
		searchTerm,
		onSearchTermChange,
		sorting,
		onSortingChange,
		canOpenHubManagement,
		isNewFilter,
		onIsNewFilterChange
	} = props;

	const [shouldShowNewAnnouncementDialog, setShouldShowNewAnnouncementDialog] = useState<boolean>(false);

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const { setViewSpecificHandlers } = useContext(HubDialogContext);
	const [reloadData, dataVersion] = useForceRerender();
	const containerRef: RefObject<HubOverviewLayoutSectionContainerHandle | null> = useRef<HubOverviewLayoutSectionContainerHandle>(null);
	const userTimezone: TimeZone = useUserTimezone();
	const [hubManagementDialog, openHubManagementDialog] = useHubManagementDialog();

	useHubItemsChangedListener(
		reloadData,
		HubItemKind.Announcement
	);

	const hasCreatePermission: boolean = useMemo(
		() => {
			const canCreateNewCategory: boolean = hubItemKindsCanCreateNewCategoriesFor.includes(HubItemKind.Announcement);

			return canCreateNewCategory || categories.some(x => x.hubItemKind === HubItemKind.Announcement && x.canCreateItem);
		},
		[categories, hubItemKindsCanCreateNewCategoriesFor]
	);

	const preferredCategoryId: number | undefined = useMemo(
		() => {
			return categories
				.find(x => (x.id === selectedCategoryId && x.canCreateItem))
				?.id;
		},
		[categories, selectedCategoryId]
	);

	const createNewHandler = useCallback(
		(): void => {
			setShouldShowNewAnnouncementDialog(true);
		},
		[]
	);

	const closeNewAnnouncementDialogHandler = useCallback(
		(wasAnyCategoryCreated: boolean): void => {
			setShouldShowNewAnnouncementDialog(false);

			if (wasAnyCategoryCreated) {
				onCategoryCreated();
			}
		},
		[onCategoryCreated]
	);

	const scrollToTopHandler = useCallback(
		(): void => containerRef.current
			?.scrollToTop(),
		[]
	);

	const isFollowing = useMemo(
		(): boolean => {
			if (categories.length === 0) {
				return false;
			}
			return selectedCategoryId === null
				? false
				: getFollowCategorySubscriptionStatus(categories, selectedCategoryId);
		},
		[categories, selectedCategoryId]
	);

	useEffect(
		(): (() => void) => {
			const backHandler: (() => void) | undefined = activeFilter
				? () => onActiveFilterChange(null)
				: undefined;

			setViewSpecificHandlers({
				backHandler
			});

			return () => {
				setViewSpecificHandlers(undefined);
			};
		},
		[
			activeFilter,
			onActiveFilterChange,
			setViewSpecificHandlers
		]
	);

	const followAction: ToolbarAction<AnnouncementActionContext> = useFollowCategoryAction<AnnouncementActionContext>(
		selectedCategoryId,
		isFollowing
	);

	const defaultActions = useMemo(
		(): readonly AnnouncementAction[] => ([
			{
				kind: ActionKind.Regular,
				name: "manage-announcement",
				title: localize("_Manage"),
				icon: "gear",
				canExecute: (x): boolean => x.canOpenHubManagement,
				execute: () => {
					openHubManagementDialog({
						initialView: "announcement.overview"
					});
				}
			},
			followAction
		]),
		[
			followAction,
			openHubManagementDialog
		]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<AnnouncementActionContext> => {
			return getNewAction(createNewHandler);
		},
		[createNewHandler]
	);

	const topBarActions = useMemo(
		(): readonly AnnouncementAction[] => {
			return buildActionsForTopBar<AnnouncementActionContext, AnnouncementAction>(
				{
					defaultActions,
					mainActions: [mainAction]
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive,
			mainAction
		]
	);

	const actionContext = useMemo(
		(): AnnouncementActionContext => ({
			activeFilter,
			hasCreatePermission,
			canOpenHubManagement,
			categoryId: selectedCategoryId
		}),
		[
			activeFilter,
			canOpenHubManagement,
			hasCreatePermission,
			selectedCategoryId
		]
	);

	const fabHandler = useMemo(
		(): (() => void) | undefined => {
			if (mainAction.canExecute(actionContext)) {
				return () => mainAction.execute(actionContext);
			}

			return undefined;
		},
		[
			actionContext,
			mainAction
		]
	);

	const selectedCategoryName: string | undefined = selectedCategoryId === null
		? localize("_AllAnnouncements")
		: getCategory(selectedCategoryId)
			?.name;

	return (
		<>
			<HubOverviewLayout trackingId="announcements-overview">
				{isMobile && (
					<ContextActionBar
						actions={topBarActions}
						context={actionContext}
						hasBorderBottom
						trackingId="global-action-bar"
					/>
				)}
				<HubOverviewLayout.Content>
					<HubOverviewLayout.Header
						key="header"
						trackingId="announcements-header"
						activeFilter={activeFilter}
						onClearActiveFilter={() => onActiveFilterChange(null)}
						title={selectedCategoryName}
						actions={topBarActions}
						actionContext={actionContext}
					>
						<SearchInputForm
							value={searchTerm}
							onChange={(value, version) => onSearchTermChange({ value, version })}
							placeholder={
								<SearchPlaceholder
									kind="filter"
									properties={[
										localize("_Title")
									]}
								/>
							}
							trackingId="search-input"
							trigger={{ kind: ChangeTriggerKind.Debounced }}
							height="large"
							icon="filter"
						/>
						<OptionsMenu
							title={localize("_FilterBy")}
							trackingId="filter-on"
							value={isNewFilter}
							onChange={onIsNewFilterChange}
							options={filterOptions}
							icon="filter"
							buttonSize="large"
						/>
						<OptionsMenu
							title={localize("_SortBy")}
							trackingId="sort-by"
							value={sorting}
							options={sortOptions}
							onChange={onSortingChange}
							icon="arrow-up-arrow-down"
							buttonSize="large"
						/>
					</HubOverviewLayout.Header>

					<HubOverviewLayout.SectionContainer
						trackingId="section-container"
						ref={containerRef}
					>
						<OverviewSections
							key={dataVersion}
							searchTerm={searchTerm.value}
							activeFilter={activeFilter}
							selectedCategoryId={selectedCategoryId}
							sorting={sorting}
							hasFloatingButton={isNotNil(fabHandler)}
							onNavigateToItem={onNavigateToItem}
							onScrollToTop={scrollToTopHandler}
							timezone={userTimezone}
							isNewFilter={isNewFilter}
						/>
						<AiAssistantFabSpacing />
					</HubOverviewLayout.SectionContainer>

					{fabHandler && (
						<FloatingActionButton
							icon="plus"
							onClick={fabHandler}
							trackingId="create-announcement-mobile-button"
						/>
					)}
				</HubOverviewLayout.Content>
			</HubOverviewLayout>
			{shouldShowNewAnnouncementDialog && (
				<CreateHubItemDialog
					preferredCategoryId={preferredCategoryId}
					kind={HubItemKind.Announcement}
					onClose={closeNewAnnouncementDialogHandler}
					onBack={closeNewAnnouncementDialogHandler}
					onSaveHubItem={reloadData}
				/>
			)}
			{hubManagementDialog}
		</>
	);
};
Overview.displayName = "Overview";

type AnnouncementActionContext = {
	activeFilter: ActiveFilter | null;
	hasCreatePermission: boolean;
	canOpenHubManagement: boolean;
	categoryId: number | null;
};

type AnnouncementAction = ToolbarAction<AnnouncementActionContext>;

function getNewAction(
	handler: () => void
): ToolbarRegularAction<AnnouncementActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "create-announcement",
		title: localize("_New"),
		icon: "plus",
		isPrimary: true,
		canExecute: (x): boolean => x.hasCreatePermission
			&& !x.activeFilter,
		execute: (): void => {
			handler();
		}
	};
}

export const filterOptions: readonly OptionsMenuItem<HubItemIsNewFilter>[] = [
	{
		value: "all",
		text: localize("_All"),
		valueTrackingId: "all-announcements"
	},
	{
		value: "new",
		text: localize("_New"),
		valueTrackingId: "new-announcements"
	}
];